import { useState, useRef, useEffect } from 'react';
import { EarningsDate } from '../api/models/EarningsDate';
import { ArrowsPointingInIcon, ArrowsPointingOutIcon, ArrowTopRightOnSquareIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { EarningsReportV2 } from '../api/models/EarningsReportV2';
import { FinancialChartModal } from './financial-chart-modal';

// Hoisted formatting helpers
export function formatDollarAmount(amount: string) {
    const num = parseFloat(amount);
    if (isNaN(num)) {
        return amount;
    }
    if (num === 0) {
        return "0";
    }
    const sign = num < 0 ? "-" : "";
    const absNum = Math.abs(num);
    const units = [
        { value: 1e9, symbol: "B" },
        { value: 1e6, symbol: "M" },
        { value: 1e3, symbol: "K" }
    ];
    for (const unit of units) {
        if (absNum >= unit.value) {
            let formatted = (absNum / unit.value).toFixed(2);
            if (formatted.endsWith('.00')) {
                formatted = formatted.slice(0, -3);
            }
            return sign + formatted + unit.symbol;
        }
    }
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatAmount(key: string, amount: string) {
    if (amount === "None" || key === "reportedCurrency" || key === 'fiscalDateEnding') return amount;
    return `$${formatDollarAmount(amount)}`;
}

function formatLabel(label: string, segment: string) {
    // Clean up revenue labels by removing common verbose suffixes
    if (label.toLowerCase().includes('revenue')) {
        label = label.replace(/from Contract with Customer, Excluding Assessed Tax/gi, '').trim();
        label = label.replace(/,\s*$/, ''); // Remove trailing comma if exists
    }
    
    if (segment) {
        // Extract after colon and before Member
        const segMatch = segment.match(/:(.*?)Member$/);
        if (segMatch && segMatch[1]) {
            let segLabel = segMatch[1]
                .replace(/([A-Z])/g, ' $1')
                .replace(/_/g, ' ')
                .replace(/\s+/g, ' ')
                .replace(/^./, s => s.toUpperCase())
                .replace(/Generation/, 'Generation') // preserve common words
                .trim();
            label += ` (${segLabel})`;
        }
    }
    return label;
}

function renderV2Section(title: string, reports: any[][], reportDates: EarningsDate[], loadNextReport: () => void, earningsReports: EarningsReportV2[], addToChart: (rowData: any, reportDates: EarningsDate[]) => void) {
    // Flatten all items and build a unique row key
    const rowMap = new Map();
    reports.forEach((section, reportIdx) => {
        if (!section) return;
        section.forEach(item => {
            if (!item.concept) return;
            const key = [item.concept, item.segment].join('|');
            if (!rowMap.has(key)) {
                rowMap.set(key, {
                    concept: item.concept,
                    segment: item.segment,
                    style: item.style,
                    level: item.level,
                    section_end: item.section_end,
                    units: item.units,
                    label: item.label,
                    values: Array(reports.length).fill(0)
                });
            }
            rowMap.get(key).values[reportIdx] = item.value;
        });
    });

    // Filter out headers
    const rows = Array.from(rowMap.values()) // .filter(row => row.node_type !== 'Header');
    return (
        <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">{title}</h2>
            <div className="overflow-x-auto relative">
                <table className="w-full text-sm text-left text-gray-500">
                    <thead>
                        <tr>
                            <th className="sticky left-0 z-20 max-w-[18rem] whitespace-nowrap overflow-hidden text-ellipsis">Metric</th>
                            {reportDates.map((reportMeta, idx) => (
                                <th key={idx} className="text-center min-w-[3.5rem]">
                                    {earningsReports[idx]?.url ? (
                                        <a
                                            href={earningsReports[idx].url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="hover:underline focus:underline cursor-pointer"
                                            style={{ color: 'inherit', textDecoration: 'underline' }}
                                            title="View original SEC filing"
                                        >
                                            {reportMeta ? `${reportMeta.fiscalPeriod} '${reportMeta.fiscalYear?.substring(2, 4)}` : ''}
                                        </a>
                                    ) : (
                                        reportMeta ? `${reportMeta.fiscalPeriod} '${reportMeta.fiscalYear?.substring(2, 4)}` : ''
                                    )}
                                </th>
                            ))}
                            <th className="text-center">
                                <button onClick={loadNextReport} className="cursor-pointer">
                                    <ChevronRightIcon className="h-5 w-5" />
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => {
                            if (row.values.every((e: number) => e === 0)) {
                                return <></>
                            }
                            let label = formatLabel(row.label, row.segment);
                            if (row.style === 'Total') label = `Total ${label}`;
                            let indent = 0; // (row.level - 1 || 0) + (row.section_end ? 1 : 0);
                            let style = { paddingLeft: `${indent * 1.5}rem`, maxWidth: '18rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
                            let isTotal = row.style === 'Total';
                            return (
                                <tr data-concept={row.concept} data-segment={row.segment} key={idx}>
                                    <td className={`${isTotal ? 'font-semibold' : ''} relative group`} style={style}>
                                        <div className="flex items-center">
                                            <span className="truncate">{label}</span>
                                            {/* Chart icon that appears on hover for metric name */}
                                            <ChartBarIcon 
                                                className="h-4 w-4 ml-1 text-blue-500 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity duration-200 flex-shrink-0"
                                                onClick={() => addToChart(row, reportDates)}
                                                title="View chart for this metric"
                                            />
                                        </div>
                                    </td>
                                    {row.values.map((value: any, colIdx: number) => {
                                        let formattedValue;
                                        if (value === '' || value == null) {
                                            if (row.concept === 'us-gaap_EarningsPerShareBasic') {
                                                console.log("ROW=>", row)
                                                console.log('Excluded: empty or null', { value, row, colIdx });
                                            }
                                            formattedValue = value || '';
                                        } else if (isNaN(Number(value))) {
                                            if (row.concept === 'us-gaap_EarningsPerShareBasic') {
                                                console.log("ROW=>", row)
                                                console.log('Excluded: not a number', { value, row, colIdx });
                                            }
                                            formattedValue = value || '';
                                        } else {
                                            if (row.units && row.units.toLowerCase().includes('usd')) {
                                                formattedValue = `$${formatDollarAmount(value)}`;
                                            } else {
                                                formattedValue = formatDollarAmount(value);
                                            }
                                        }
                                        return (
                                            <td className="text-right relative group" key={colIdx}>
                                                <div className="flex items-center justify-end">
                                                    <span>{formattedValue}</span>
                                                    {/* Chart icon that appears on hover */}
                                                    <ChartBarIcon 
                                                        className="h-4 w-4 ml-1 text-blue-500 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity duration-200"
                                                        onClick={() => addToChart(row, reportDates)}
                                                        title="View chart for this metric"
                                                    />
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export const EarningsReportComponentV2 = ({ supportedTickers, setCurrentTicker, earningsReports, setFiscalPeriod, setFiscalYear, fiscalYear, fiscalPeriod, earningsDates, onViewChange, loadNextReport, setTableView, tableView, currentTicker }:
    {
        supportedTickers: string[];
        setCurrentTicker: (ticker: string) => void;
        earningsReports: EarningsReportV2[];
        fiscalYear: string;
        fiscalPeriod: string;
        setFiscalYear: (year: string) => void;
        setFiscalPeriod: (period: string) => void;
        earningsDates: EarningsDate[];
        onViewChange?: (isTableView: boolean) => void;
        loadNextReport: () => void;
        tableView: boolean;
        setTableView: (v: boolean) => void;
        currentTicker: string;
    }) => {
    const [chartModal, setChartModal] = useState({
        isOpen: false,
        datasets: [] as Array<{label: string, units?: string, color: string}>,
        data: [] as Array<{period: string, [key: string]: string | number}>
    });
    const [tickerSearch, setTickerSearch] = useState('');
    const [isTickerDropdownOpen, setIsTickerDropdownOpen] = useState(false);
    const tickerDropdownRef = useRef<HTMLDivElement>(null);

    // Filter tickers based on search
    const filteredTickers = supportedTickers.filter(ticker =>
        ticker.toLowerCase().includes(tickerSearch.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tickerDropdownRef.current && !tickerDropdownRef.current.contains(event.target as Node)) {
                setIsTickerDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    
    const addToChart = (rowData: any, reportDates: EarningsDate[]) => {
        const label = formatLabel(rowData.label, rowData.segment);
        const metricLabel = rowData.style === 'Total' ? `Total ${label}` : label;
        
        // Check if this metric is already in the chart
        if (chartModal.datasets.some(d => d.label === metricLabel)) {
            // If metric already exists, just reopen the modal
            setChartModal(prev => ({ ...prev, isOpen: true }));
            return;
        }
        
        // Colors for different datasets
        const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];
        const newColor = colors[chartModal.datasets.length % colors.length];
        
        // Create or update chart data
        let updatedData = [...chartModal.data];
        
        // If this is the first dataset, create the data structure
        if (chartModal.datasets.length === 0) {
            updatedData = reportDates.map((reportMeta, idx) => {
                const value = rowData.values[idx];
                let numericValue = 0;
                
                if (value !== '' && value != null && !isNaN(Number(value))) {
                    numericValue = Number(value);
                }
                
                // Create period label with proper FY identification
                let periodLabel = `Period ${idx + 1}`;
                if (reportMeta) {
                    // Debug logging to see what we're getting
                    console.log('Period data:', {
                        fiscalPeriod: reportMeta.fiscalPeriod,
                        fiscalYear: reportMeta.fiscalYear,
                        date: reportMeta.date,
                        index: idx
                    });
                    
                    periodLabel = `${reportMeta.fiscalPeriod} '${reportMeta.fiscalYear?.substring(2, 4)}`;
                }
                
                return {
                    period: periodLabel,
                    [metricLabel]: numericValue
                };
            });
        } else {
            // Add this metric to existing data
            updatedData = updatedData.map((dataPoint, idx) => {
                const value = rowData.values[idx];
                let numericValue = 0;
                
                if (value !== '' && value != null && !isNaN(Number(value))) {
                    numericValue = Number(value);
                }
                
                return {
                    ...dataPoint,
                    [metricLabel]: numericValue
                };
            });
        }
        
        setChartModal({
            isOpen: true,
            datasets: [...chartModal.datasets, { label: metricLabel, units: rowData.units || '', color: newColor }],
            data: updatedData
        });
    };
    
    const removeFromChart = (metricLabel: string) => {
        const updatedDatasets = chartModal.datasets.filter(d => d.label !== metricLabel);
        const updatedData = chartModal.data.map(point => {
            const newPoint = { ...point };
            delete newPoint[metricLabel];
            return newPoint;
        });
        
        setChartModal({
            ...chartModal,
            datasets: updatedDatasets,
            data: updatedData
        });
        
        // Close modal if no datasets left
        if (updatedDatasets.length === 0) {
            setChartModal({
                isOpen: false,
                datasets: [],
                data: []
            });
        }
    };
    
    // const [tableView, setTableView] = useState(false);

    const toggleView = () => {
        const newView = !tableView;
        setTableView(newView);
        if (onViewChange) {
            onViewChange(newView);
        }
    };

    const renderEmptyState = () => (
        <div className="data-empty-state">
            <span>Financials</span>
            <h3>No earnings data for this period</h3>
            <p>Select another reporting date or company to review its filing data.</p>
        </div>
    );

    const renderOldView = () => {
        const report = earningsReports[0];
        if (!report) return renderEmptyState();

        // For old view, handle array of objects (new format)
        const renderOldSection = (title: string, data: any[]) => {
            if (!data || !Array.isArray(data)) return null;
            return (
                <div>
                    <h2 className="text-xl font-semibold mb-2">{title}</h2>
                    <div className="space-y-1">
                        {data.filter(item => item.concept && item.node_type !== 'Header').map((item, idx) => {
                            let label = formatLabel(item.label, item.segment);
                            if (item.node_type === 'Total') label = `Total ${label}`;
                            let indent = 0; //(item.level || 0) //+ (item.section_end ? 1 : 0);
                            let labelStyle = { maxWidth: '18rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', verticalAlign: 'bottom' };
                            let containerStyle = { paddingLeft: `${indent * 1.5}rem` };
                            let isTotal = item.node_type === 'Total';
                            let value = item.value;
                            let formattedValue = value !== '' && !isNaN(Number(value))
                                ? ((item.units && item.units.toLowerCase().includes('usd'))
                                    ? `$${formatDollarAmount(value)}`
                                    : formatDollarAmount(value))
                                : value;
                            return (
                                <div key={idx} className={isTotal ? 'font-semibold' : ''} style={containerStyle} title={label}>
                                    <span style={labelStyle}>{label}</span>
                                    <span className="float-right">
                                        {formattedValue}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        };

        return (
            <>
                {renderOldSection('Balance Sheet', report.balanceSheet)}
                {renderOldSection('Income Statement', report.incomeStatement)}
                {renderOldSection('Cash Flow Statement', report.cashflows)}
                {renderOldSection('Comprehensive Income', report.comprehensiveIncome)}
                {renderOldSection('Changes In Equity', report.changesInEquity)}
            </>
        );
    };

    const renderV2 = () => {
        if (!earningsReports || earningsReports.length === 0) return renderEmptyState();
        // For each section, collect arrays from all reports, defaulting to [] if missing
        const reportDates: EarningsDate[] = earningsReports.map(r => ({
            date: r.fiscalDateEnding,
            fiscalPeriod: r.fiscalPeriod,
            fiscalYear: r.fiscalYear,
            userHasAccess: true
        }));
        const getSectionArrays = (sectionName: keyof EarningsReportV2): any[][] => {
            const result: any[][] = [];
            earningsReports.forEach(r => {
                const section = r[sectionName];
                if (Array.isArray(section)) {
                    result.push(section);
                } else {
                    result.push([]);
                }
            });
            return result;
        };
        return (
            <>
                {renderV2Section('Income Statement', getSectionArrays('incomeStatement'), reportDates, loadNextReport, earningsReports, addToChart)}
                {renderV2Section('Balance Sheet', getSectionArrays('balanceSheet'), reportDates, loadNextReport, earningsReports, addToChart)}
                {renderV2Section('Cash Flow Statement', getSectionArrays('cashflows'), reportDates, loadNextReport, earningsReports, addToChart)}
                {renderV2Section('Comprehensive Income', getSectionArrays('comprehensiveIncome'), reportDates, loadNextReport, earningsReports, addToChart)}
                {renderV2Section('Changes In Equity', getSectionArrays('changesInEquity'), reportDates, loadNextReport, earningsReports, addToChart)}
            </>
        );
    };

    const formatFpFyForListKey = (fiscalPeriod: string, fiscalYear: string) => {
        return `${fiscalPeriod}-'${fiscalYear?.substring(2, 4)}`
    }

    return (
        <div className="research-report flex flex-col h-full overflow-hidden pl-4">
            {/* Sticky Header */}
            <div className="research-panel-heading flex items-center justify-between sticky top-0 z-30 py-4">
                <div className="research-title-row">
                <div>
                    <span>Financials</span>
                    <h2 className="font-bold">Earnings report</h2>
                </div>
                <div className="research-heading-actions flex items-center">
                    <div className="flex items-center">
                        {!tableView && (
                        <>
                            <button type="button" title="Expand earnings report" aria-label="Expand earnings report" onClick={toggleView}>
                                <ArrowsPointingOutIcon className='w-5 h-5 ml-2 cursor-pointer text-slate-700 hover:text-emerald-700' />
                            </button>
                            <button
                                type="button"
                                title={earningsReports[0]?.url ? "Open source filing" : "No source filing available"}
                                aria-label="Open source filing"
                                disabled={!earningsReports[0]?.url}
                                onClick={() => window.open(earningsReports[0]?.url, "_blank", "noopener,noreferrer")}
                            >
                                <ArrowTopRightOnSquareIcon className={`h-6 w-6 mb-1 ml-2 text-slate-700 ${earningsReports[0]?.url ? 'cursor-pointer hover:text-emerald-700' : 'cursor-not-allowed opacity-40'}`} />
                            </button>
                        </>
                        )}
                        {tableView && (
                        <button type="button" title="Collapse earnings report" aria-label="Collapse earnings report" onClick={toggleView}>
                            <ArrowsPointingInIcon className='w-5 h-5 ml-2 cursor-pointer' />
                        </button>
                        )}
                    </div>
                </div>
                </div>
                <div className="research-controls relative w-80 flex items-center mb-2">
                {/* Searchable Ticker Dropdown */}
                <div className="relative w-1/2" ref={tickerDropdownRef}>
                    <input
                        type="text"
                        className="earnings-ticker-input w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 cursor-pointer"
                        value={isTickerDropdownOpen ? tickerSearch : (currentTicker || '')}
                        onChange={(e) => {
                            setTickerSearch(e.target.value);
                            setIsTickerDropdownOpen(true);
                        }}
                        onFocus={() => {
                            setTickerSearch('');
                            setIsTickerDropdownOpen(true);
                        }}
                        placeholder="Search ticker..."
                        readOnly={!isTickerDropdownOpen}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                    
                    {isTickerDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {filteredTickers.length > 0 ? (
                                filteredTickers.map((ticker) => (
                                    <div
                                        key={ticker}
                                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                                        onClick={() => {
                                            setCurrentTicker(ticker);
                                            setIsTickerDropdownOpen(false);
                                            setTickerSearch('');
                                        }}
                                    >
                                        {ticker}
                                    </div>
                                ))
                            ) : (
                                <div className="px-3 py-2 text-sm text-gray-500">
                                    No tickers found
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {!tableView && (
                    <select
                        className="w-full ml-2 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 appearance-none"
                        value={formatFpFyForListKey(fiscalPeriod, fiscalYear)}
                        onChange={(e) => {
                            const option = e.target.selectedOptions[0];
                            const fp = option.dataset.fp;
                            const fy = option.dataset.fy;
                            if (!fp || !fy) {
                                console.error("fp or fy was not set in the onchange handler")
                                return;
                            }
                            setFiscalPeriod(fp);
                            setFiscalYear(fy);
                        }}
                    >
                        {earningsDates?.map((item) => (
                            <option
                                disabled={!item.userHasAccess}
                                key={`${item.date}-${item.fiscalYear}-${item.fiscalPeriod}`}
                                value={formatFpFyForListKey(item.fiscalPeriod!, item.fiscalYear!)}
                                data-fp={item.fiscalPeriod}
                                data-fy={item.fiscalYear}
                            >
                                {item.date} ({item.fiscalPeriod}-'{item.fiscalYear?.substring(2, 4)})
                            </option>
                        ))}
                    </select>
                )}
                </div>
            </div>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto space-y-6 pr-4">
                {earningsReports && earningsReports.length > 0 ? (
                    tableView ? (
                        renderV2()
                    ) : (
                        renderOldView()
                    )
                ) : renderEmptyState()}
            </div>
            
            {/* Chart Modal */}
            <FinancialChartModal
                isOpen={chartModal.isOpen}
                onClose={() => setChartModal(prev => ({ ...prev, isOpen: false }))}
                data={chartModal.data}
                datasets={chartModal.datasets}
                onRemoveDataset={removeFromChart}
            />
        </div>
    );
};
